import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Chip,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddIcon from '@mui/icons-material/Add';
import { useSampleNotificationTemplate } from 'shared/apollo';
import Editor from './Editor';
import { getDefaultTemplate, getDefaultSubject } from './default';

const variables = [
  { key: 'investmentFirstName', help: 'Donor\'s First Name.' },
  { key: 'projectManager', help: 'Project Manager\'s Full name.' },
  { key: 'donorAccountName', help: 'Donor\'s Company name.' },
  {
    key: 'investmentRecipientOrganization',
    help: 'Name of the organization receiving the donation/contribution.',
  },
  {
    key: 'investmentFundedProgramName',
    help: 'Name of the program receiving the donation/contribution.',
  },
  {
    key: 'investmentAmount',
    help: 'The amount being contributed by the donor.',
  },
  {
    key: 'investmentRecipientEmail',
    help:
      'Email address of the NPO/organization manager receiving the donation/contribution.',
  },
  { key: 'reportStage', help: 'Current stage of the report.' },
  { key: 'submitForFeedback', help: 'The due date for submit for feedback.' },
  { key: 'resolveFeedback', help: 'The due date for resolve feedback.' },
  {
    key: 'interimReportUpdate',
    help: 'The due date for interim report update.',
  },
  { key: 'finalizeReportDate', help: 'The due date for finalize report.' },
  {
    key: 'reportName',
    help: 'The name of the report provided by the non-profit organization.',
  },
  { key: 'reportStatus', help: 'Current status of the report.' },
  { key: 'portfolioName', help: 'Contibution\'s Portfolio name.' },
  { key: 'publishDueDate', help: 'Due date to publish the report.' },
];

const CustomTemplatePopup = ({
  open,
  values,
  rule,
  customStatusRule,
  handleUpdate = () => { /* noop */ },
  onClose = () => { /* noop */ },
  onDone = () => { /* noop */ },
}) => {
  const editorRef = useRef();
  const [currentVar, setCurrentVar] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [focus, setFocus] = useState('body');
  const [showPreview, setShowPreview] = useState(false);
  const [previewError, setPreviewError] = useState();
  const [sampleSent, setSampleSent] = useState(false);
  const { run: runSampleNotification } = useSampleNotificationTemplate();

  useEffect(() => {
    const initialBody =
      values?.notificationBody || getDefaultTemplate(rule, customStatusRule);
    setBody(initialBody);
  }, [
    values?.notificationBody,
    rule,
    customStatusRule,
    getDefaultTemplate,
    setBody,
  ]);

  useEffect(() => {
    const initialSubject =
      values?.notificationSubject || getDefaultSubject(rule, customStatusRule);
    setSubject(initialSubject);
  }, [values?.notificationSubject, rule, customStatusRule]);

  const handleAddVariable = () => {
    if (!currentVar) return;
    if (focus === 'body') {
      const editorFn = editorRef?.current?.updateBody;
      const editorPushVar = editorRef?.current?.pushVar;
      if (editorFn && typeof editorFn == 'function') {
        let newBody = body;
        if (body.trim().endsWith('</p>')) {
          newBody = body.trim().slice(0, -4) + ` &lt${currentVar}&gt</p>`;
          editorFn(newBody);
        } else {
          editorPushVar(`<${currentVar}>`);
        }
      }
    } else {
      setSubject(`${subject} <${currentVar}>`);
    }
    setCurrentVar('');
  };

  const handlePreview = () => {
    if (!subject) {
      return setPreviewError('A subject is required for your notification');
    }
    if (!body) {
      return setPreviewError('A body is required for your notification');
    }
    handleUpdate(subject, body);
    setPreviewError();
    setShowPreview(true);
  };

  const handleSendSample = () => {
    setSampleSent(true);
    runSampleNotification({
      id: values?.templateId,
      name: subject,
      template: body,
    });
  };

  const handleDone = () => {
    if (!subject) {
      return setPreviewError('A subject is required for your notification');
    }
    if (!body) {
      return setPreviewError('A body is required for your notification');
    }
    setPreviewError();
    onDone(subject, body);
  };

  const handleChangeVar = event => {
    setCurrentVar(event.target.value);
  };

  const replaceInString = string => {
    let text = string;
    text = text.replace(new RegExp('<investmentFirstName>', 'g'), 'Jon Doe');
    text = text.replace(new RegExp('<projectManager>', 'g'), 'Jane Doe');
    text = text.replace(new RegExp('<donorAccountName>', 'g'), 'Company Inc.');
    text = text.replace(
      new RegExp('<investmentRecipientOrganization>', 'g'),
      'Awesome Org.',
    );
    text = text.replace(
      new RegExp('<investmentFundedProgramName>', 'g'),
      'Jane Progrom',
    );
    text = text.replace(new RegExp('<investmentAmount>', 'g'), '$100.000,00');
    text = text.replace(
      new RegExp('<investmentRecipientEmail>', 'g'),
      'janedoe@awesomeorg.com',
    );
    text = text.replace(new RegExp('<reportStage>', 'g'), 'Initial');
    text = text.replace(
      new RegExp('<submitForFeedback>', 'g'),
      'July 10, 2023',
    );
    text = text.replace(new RegExp('<resolveFeedback>', 'g'), 'July 10, 2023');
    text = text.replace(
      new RegExp('<interimReportUpdate>', 'g'),
      'July 10, 2023',
    );
    text = text.replace(
      new RegExp('<finalizeReportDate>', 'g'),
      'July 10, 2023',
    );
    text = text.replace(new RegExp('<reportName>', 'g'), 'My Report');
    text = text.replace(new RegExp('<reportStatus>', 'g'), 'Draft');
    text = text.replace(new RegExp('<portfolioName>', 'g'), 'My Portfolio');
    text = text.replace(new RegExp('<publishDueDate>', 'g'), 'July 10, 2023');
    return text;
  };

  const handleEditorChange = htmlText => {
    setFocus('body');
    setBody(htmlText);
  };

  return (
    <DialogRoot
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose?.();
      }}
      open={open}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle onClose={onClose}>
        Custom Email Template
        <CloseIcon className="close" onClick={onClose} />
      </DialogTitle>
      {showPreview && (
        <DialogContent dividers>
          <PreviewContainer>
            <p class="small">Subject</p>
            <div
              dangerouslySetInnerHTML={{ __html: replaceInString(subject) }}
            />
            <p></p>
          </PreviewContainer>
          <PreviewContainer>
            <p>
              <div
                dangerouslySetInnerHTML={{ __html: replaceInString(body) }}
              />
            </p>
          </PreviewContainer>
          {sampleSent && (
            <Chip
              className="success"
              color="primary"
              icon={<MailOutlineIcon />}
              label="Sample notification sent"
              variant="outlined"
            />
          )}
        </DialogContent>
      )}
      {!showPreview && (
        <DialogContent dividers>
          <Grid container direction="row" justify="center" alignItems="center">
            <Grid item xs={12}>
              <p>
                Variables: <span>(Click to add on focused field)</span>
              </p>
              {/* variables.map(variable => <Chip
                  label={variable.key}
                  clickable
                  color="primary"
                  onDelete={() => {}}
                  onClick={() => handleAddVariable(variable.key)}
                  deleteIcon={<Tooltip title={variable.help} placement="top" arrow><HelpOutlineIcon/></Tooltip>}
                />
              )*/}
              <div className="flex">
                <FormControl variant="outlined">
                  <InputLabel id="variables-label">Add variable</InputLabel>
                  <Select
                    labelId="variables-label"
                    id="variables-label-select"
                    value={currentVar}
                    onChange={handleChangeVar}
                    label="Add variable"
                  >
                    <MenuItem value="">
                      <em>Choose one...</em>
                    </MenuItem>
                    {variables.map(({ key }) => (
                      <MenuItem key={key} value={key}>
                        {key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  disabled={currentVar === ''}
                  onClick={handleAddVariable}
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </div>
              <p>
                <span>
                  {variables.filter(v => v.key === currentVar)?.[0]?.help ??
                    'Choose a variable to know more...'}
                </span>
              </p>
            </Grid>
            <Grid item xs={12}>
              <p>Subject:</p>
              <TextField
                onClick={() => setFocus('subject')}
                placeholder="Your notification subject"
                fullWidth
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <p className="mt20">Body Text:</p>
              <div>
                <Editor
                  onChange={handleEditorChange}
                  initialValue={body}
                  ref={editorRef}
                />
              </div>
            </Grid>
          </Grid>
          {previewError && (
            <Chip
              className="error"
              icon={<ErrorOutlineIcon />}
              label={previewError}
              variant="outlined"
            />
          )}
        </DialogContent>
      )}
      <DialogActions>
        {!showPreview && (
          <Button
            onClick={handlePreview}
            variant="outlined"
            color="primary"
            startIcon={<VisibilityIcon />}
          >
            Preview Template
          </Button>
        )}
        {showPreview && (
          <Button
            onClick={() => setShowPreview(false)}
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        )}
        {!sampleSent && showPreview && (
          <Button
            className="floatLeft"
            onClick={handleSendSample}
            variant="outlined"
            color="primary"
            startIcon={<MailOutlineIcon />}
          >
            Send Test Message To Me
          </Button>
        )}
        <Button
          onClick={handleDone}
          variant="contained"
          color="primary"
          startIcon={<DoneIcon />}
        >
          Done
        </Button>
      </DialogActions>
    </DialogRoot>
  );
};

const DialogRoot = styled(Dialog)`
  div.MuiDialogTitle-root {
    background-color: #ebf0f6;
    color: #48596b;
    font-weight: 400;
    font-size: 16px;
  }
  div.MuiInputBase-root {
    min-height: 54px;
  }
  p {
    color: rgb(72, 89, 107);
    font-size: 16px;
    font-weight: 700;
    &.mt20 {
      margin-top: 20px;
    }
    span {
      font-size: 15px;
      font-weight: 400;
    }
  }
  svg.close {
    color: rgb(72, 89, 107);
    float: right;
    font-size: 25px;
    transform: translateY(5px);
  }
  button {
    min-height: 48px;
    padding-left: 25px;
    padding-right: 25px;
    text-transform: none;
    &.floatLeft {
      position: absolute;
      left: 16px;
    }
  }
  .MuiChip-clickable {
    margin-right: 5px;
    margin-bottom: 5px;
  }
  .MuiChip-root.error {
    margin-top: 16px;
    color: #ff4d4f;
    border: solid 1px #ff4d4f;
    svg {
      color: #ff4d4f;
    }
  }
  .MuiChip-root.success {
    margin-top: 16px;
  }
  .MuiFormControl-root {
    width: 100%;
  }
  div.flex {
    display: flex;
    flex-direction: row;
    button {
      margin-left: 10px;
    }
  }
  .rdw-editor-main {
    border: solid 1px #bbb;
    border-radius: 5px;
    padding-left: 10px;
    padding-right: 10px;
    min-height: 250px;
  }
  div.rdw-left-aligned-block {
    height: 0px;
  }
  .demo-wrapper {
    .rdw-link-modal {
      height: auto !important;
    }
    .rdw-embedded-modal {
      height: auto !important;
    }
    .rdw-colorpicker-modal-options {
      overflow: auto;
    }
    .rdw-colorpicker-modal-options::-webkit-scrollbar {
      display: none;
    }
    a {
      color: #1890ff;
      font-weight: bold;
    }
  }
`;

const PreviewContainer = styled.div`
  width: 100%;
  height: auto;
  background-color: #ebf0f6;
  padding: 10px;
  border-radius: 5px;
  margin-top: 10px;
  p {
    color: rgb(72, 89, 107);
    font-size: 17px;
    font-weight: 400;
    &.small {
      font-size: 14px;
      font-weight: 700;
    }
  }
`;

export default CustomTemplatePopup;
